// frontend/src/pages/Tasks.js
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AuthContext from '../context/AuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    CheckboxIcon as TaskIcon,
    AddIcon as Add,
    DeleteIcon as Delete,
    CheckIcon as Check,
    CloseIcon as X,
    CalendarIcon as Calendar
} from '../components/Icons';

const Tasks = () => {
    const { API_URL, user } = useContext(AuthContext);
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showModal, setShowModal] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [filter, setFilter] = useState('all');
    const [users, setUsers] = useState([]);

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        priority: 'medium',
        deadline: '',
        assignees: [],
        subtasks: []
    });

    const priorityOptions = [
        { value: 'low', label: 'Niedrig', color: 'bg-surface-variant text-surface-onVariant' },
        { value: 'medium', label: 'Mittel', color: 'bg-secondary-container text-secondary-onContainer' },
        { value: 'high', label: 'Hoch', color: 'bg-tertiary-container text-tertiary-onContainer' },
        { value: 'urgent', label: 'Dringend', color: 'bg-error-container text-error-on-container' }
    ];

    const statusOptions = [
        { value: 'todo', label: 'Offen', color: 'bg-surface-variant' },
        { value: 'in_progress', label: 'In Bearbeitung', color: 'bg-secondary-container' },
        { value: 'review', label: 'Review', color: 'bg-tertiary-container' },
        { value: 'done', label: 'Erledigt', color: 'bg-primary-container' }
    ];

    useEffect(() => {
        fetchTasks();
        fetchUsers();
        // eslint-disable-next-line
    }, [filter]);

    const fetchTasks = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const url = filter === 'my'
                ? `${API_URL}/tasks/my`
                : `${API_URL}/tasks${filter !== 'all' ? `?status=${filter}` : ''}`;
            const { data } = await axios.get(url, config);
            setTasks(data);
            setLoading(false);
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Laden' });
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            const { data } = await axios.get(`${API_URL}/users`, config);
            setUsers(data.filter(u => u.role === 'gruppenleiter' || u.role === 'admin'));
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };

            if (selectedTask) {
                await axios.put(`${API_URL}/tasks/${selectedTask._id}`, formData, config);
                setMessage({ type: 'success', text: 'Aufgabe aktualisiert!' });
            } else {
                await axios.post(`${API_URL}/tasks`, formData, config);
                setMessage({ type: 'success', text: 'Aufgabe erstellt!' });
            }

            setShowModal(false);
            resetForm();
            fetchTasks();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Speichern' });
        }
    };

    const updateStatus = async (taskId, newStatus) => {
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.put(`${API_URL}/tasks/${taskId}`, { status: newStatus }, config);
            fetchTasks();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Aktualisieren' });
        }
    };

    const handleDragEnd = async (result) => {
        const { destination, source, draggableId } = result;
        if (!destination) return;
        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        const taskId = draggableId;
        const newStatus = destination.droppableId;

        // Optimistically update status in state
        const updatedTasks = tasks.map(t => {
            if (t._id === taskId) {
                return { ...t, status: newStatus };
            }
            return t;
        });
        setTasks(updatedTasks);

        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.put(`${API_URL}/tasks/${taskId}`, { status: newStatus }, config);
            fetchTasks();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Verschieben der Aufgabe' });
            fetchTasks();
        }
    };

    const deleteTask = async (taskId) => {
        if (!window.confirm('Aufgabe wirklich löschen?')) return;
        try {
            const config = { headers: { Authorization: `Bearer ${user.token}` } };
            await axios.delete(`${API_URL}/tasks/${taskId}`, config);
            setMessage({ type: 'success', text: 'Aufgabe gelöscht!' });
            fetchTasks();
        } catch (error) {
            setMessage({ type: 'error', text: 'Fehler beim Löschen' });
        }
    };

    const openEditModal = (task) => {
        setSelectedTask(task);
        setFormData({
            title: task.title,
            description: task.description || '',
            priority: task.priority,
            deadline: task.deadline ? task.deadline.split('T')[0] : '',
            assignees: task.assignees?.map(a => a._id) || [],
            subtasks: task.subtasks || []
        });
        setShowModal(true);
    };

    const resetForm = () => {
        setSelectedTask(null);
        setFormData({
            title: '',
            description: '',
            priority: 'medium',
            deadline: '',
            assignees: [],
            subtasks: []
        });
    };

    const addSubtask = () => {
        setFormData({
            ...formData,
            subtasks: [...formData.subtasks, { title: '', isCompleted: false }]
        });
    };

    const getPriorityBadge = (priority) => {
        const opt = priorityOptions.find(p => p.value === priority);
        return opt ? opt.color : '';
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    // Group tasks by status for Kanban view
    const tasksByStatus = statusOptions.reduce((acc, status) => {
        acc[status.value] = tasks.filter(t => t.status === status.value);
        return acc;
    }, {});

    return (
        <div className="container mx-auto p-4 max-w-7xl animate-fade-in">
            {/* Header */}
            <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-xl bg-primary-container text-primary-onContainer flex items-center justify-center shadow-sm">
                        <TaskIcon style={{ fontSize: 28 }} />
                    </div>
                    <div>
                        <h1 className="text-display-small font-bold text-surface-on">Aufgaben</h1>
                        <p className="text-body-medium text-surface-onVariant">{tasks.length} Aufgaben</p>
                    </div>
                </div>

                <button
                    onClick={() => { resetForm(); setShowModal(true); }}
                    className="md3-btn-filled flex items-center gap-2"
                >
                    <Add style={{ fontSize: 20 }} />
                    Neue Aufgabe
                </button>
            </div>

            {/* Alert */}
            {message.text && (
                <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 animate-fade-in ${message.type === 'error' ? 'bg-error-container text-error-on-container' : 'bg-primary-container text-primary-onContainer'
                    }`}>
                    <span>{message.text}</span>
                    <button onClick={() => setMessage({ type: '', text: '' })} className="ml-auto">
                        <X style={{ fontSize: 18 }} />
                    </button>
                </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                <button
                    onClick={() => setFilter('all')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${filter === 'all' ? 'bg-primary text-white' : 'bg-surface-variant text-surface-onVariant hover:bg-surface-containerLow'
                        }`}
                >
                    Alle
                </button>
                <button
                    onClick={() => setFilter('my')}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${filter === 'my' ? 'bg-primary text-white' : 'bg-surface-variant text-surface-onVariant hover:bg-surface-containerLow'
                        }`}
                >
                    Meine Aufgaben
                </button>
                {statusOptions.map(s => (
                    <button
                        key={s.value}
                        onClick={() => setFilter(s.value)}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${filter === s.value ? 'bg-primary text-white' : 'bg-surface-variant text-surface-onVariant hover:bg-surface-containerLow'
                            }`}
                    >
                        {s.label}
                    </button>
                ))}
            </div>

            {/* Kanban Board */}
            <DragDropContext onDragEnd={handleDragEnd}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {statusOptions.map(status => (
                        <div key={status.value} className="md3-card-outlined p-4 flex flex-col">
                            <div className={`flex items-center gap-2 mb-4 px-3 py-2 rounded-lg ${status.color}`}>
                                <h3 className="font-bold">{status.label}</h3>
                                <span className="ml-auto text-sm font-medium">{tasksByStatus[status.value]?.length || 0}</span>
                            </div>

                            <Droppable droppableId={status.value}>
                                {(provided, snapshot) => (
                                    <div
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                        className={`space-y-3 min-h-[250px] flex-1 transition-colors rounded-lg p-1 ${
                                            snapshot.isDraggingOver ? 'bg-surface-containerHigh/40' : ''
                                        }`}
                                    >
                                        {tasksByStatus[status.value]?.map((task, index) => (
                                            <Draggable key={task._id} draggableId={task._id} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className={`md3-card-elevated p-4 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow select-none ${
                                                            snapshot.isDragging ? 'shadow-elevation-4 border border-primary/30 rotate-2' : ''
                                                        }`}
                                                        onClick={() => openEditModal(task)}
                                                        style={{
                                                            ...provided.draggableProps.style,
                                                            userSelect: 'none'
                                                        }}
                                                    >
                                                        {/* Priority & Deadline */}
                                                        <div className="flex items-center justify-between mb-2">
                                                            <span className={`px-2 py-0.5 rounded text-label-small font-medium ${getPriorityBadge(task.priority)}`}>
                                                                {priorityOptions.find(p => p.value === task.priority)?.label}
                                                            </span>
                                                            {task.deadline && (
                                                                <span className={`text-label-small flex items-center gap-1 ${task.isOverdue ? 'text-error' : 'text-surface-onVariant'
                                                                    }`}>
                                                                    <Calendar style={{ fontSize: 12 }} />
                                                                    {new Date(task.deadline).toLocaleDateString('de-DE')}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Title */}
                                                        <h4 className="font-bold text-body-large mb-2 line-clamp-2">{task.title}</h4>

                                                        {/* Progress */}
                                                        {Array.isArray(task.subtasks) && task.subtasks.length > 0 && (
                                                            <div className="mb-3">
                                                                <div className="h-1.5 bg-surface-variant rounded-full overflow-hidden">
                                                                    <div
                                                                        className="h-full bg-primary transition-all"
                                                                        style={{ width: `${task.progress || 0}%` }}
                                                                    />
                                                                </div>
                                                                <p className="text-label-small text-surface-onVariant mt-1">
                                                                    {task.subtasks.filter(s => s.isCompleted).length}/{task.subtasks.length} Unteraufgaben
                                                                </p>
                                                            </div>
                                                        )}

                                                        {/* Assignees */}
                                                        {task.assignees?.length > 0 && (
                                                            <div className="flex -space-x-2">
                                                                {task.assignees.slice(0, 3).map((a, idx) => (
                                                                    <div
                                                                        key={a._id || idx}
                                                                        className="w-6 h-6 rounded-full bg-secondary-container text-secondary-onContainer text-xs font-bold flex items-center justify-center border border-surface"
                                                                        title={a.userData?.scoutName || a.userData?.firstName || 'Nutzer'}
                                                                    >
                                                                        {(a.userData?.scoutName?.[0] || a.userData?.firstName?.[0] || 'U').toUpperCase()}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}

                                                        {/* Quick Actions */}
                                                        <div className="flex gap-1 mt-3 pt-3 border-t border-outlineVariant">
                                                            {status.value !== 'done' && (
                                                                <button
                                                                    onClick={(e) => { e.stopPropagation(); updateStatus(task._id, 'done'); }}
                                                                    className="md3-icon-btn text-primary hover:bg-primary-container"
                                                                    title="Als erledigt markieren"
                                                                >
                                                                    <Check style={{ fontSize: 18 }} />
                                                                </button>
                                                            )}
                                                            <button
                                                                    onClick={(e) => { e.stopPropagation(); deleteTask(task._id); }}
                                                                    className="md3-icon-btn text-error hover:bg-error-container ml-auto"
                                                                    title="Löschen"
                                                                >
                                                                    <Delete style={{ fontSize: 18 }} />
                                                                </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </div>
                    ))}
                </div>
            </DragDropContext>

            {/* Create/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-scrim/40 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-fade-in">
                    <div className="md3-card-elevated max-w-2xl w-full p-8 animate-scale-in max-h-[90vh] overflow-y-auto">
                        <h2 className="text-title-large font-bold mb-6">
                            {selectedTask ? 'Aufgabe bearbeiten' : 'Neue Aufgabe'}
                        </h2>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Titel *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    className="md3-textfield-outlined"
                                    required
                                />
                            </div>

                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Beschreibung</label>
                                <textarea
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    className="md3-textfield-outlined min-h-[100px]"
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Priorität</label>
                                    <select
                                        value={formData.priority}
                                        onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                                        className="md3-textfield-outlined"
                                    >
                                        {priorityOptions.map(p => (
                                            <option key={p.value} value={p.value}>{p.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-label-large text-surface-onVariant mb-2">Deadline</label>
                                    <input
                                        type="date"
                                        value={formData.deadline}
                                        onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                                        className="md3-textfield-outlined"
                                    />
                                </div>
                            </div>

                            {/* Assignees */}
                            <div>
                                <label className="block text-label-large text-surface-onVariant mb-2">Zugewiesen an</label>
                                <div className="flex flex-wrap gap-2">
                                    {users.map(u => (
                                        <button
                                            key={u._id}
                                            type="button"
                                            onClick={() => setFormData({
                                                ...formData,
                                                assignees: formData.assignees.includes(u._id)
                                                    ? formData.assignees.filter(id => id !== u._id)
                                                    : [...formData.assignees, u._id]
                                            })}
                                            className={`px-3 py-1.5 rounded-full text-sm transition-colors ${formData.assignees.includes(u._id)
                                                    ? 'bg-primary text-white'
                                                    : 'bg-surface-variant text-surface-onVariant hover:bg-surface-containerLow'
                                                }`}
                                        >
                                            {u.userData?.scoutName || u.userData?.firstName || 'Unbekannt'}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Subtasks */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <label className="text-label-large text-surface-onVariant">Unteraufgaben</label>
                                    <button type="button" onClick={addSubtask} className="md3-btn-text text-sm">
                                        <Add style={{ fontSize: 16 }} className="mr-1" />
                                        Hinzufügen
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {formData.subtasks.map((sub, idx) => (
                                        <div key={idx} className="flex items-center gap-2">
                                            <input
                                                type="text"
                                                value={sub.title}
                                                onChange={(e) => {
                                                    const newSubs = [...formData.subtasks];
                                                    newSubs[idx].title = e.target.value;
                                                    setFormData({ ...formData, subtasks: newSubs });
                                                }}
                                                className="md3-textfield-outlined flex-1"
                                                placeholder="Unteraufgabe..."
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setFormData({
                                                    ...formData,
                                                    subtasks: formData.subtasks.filter((_, i) => i !== idx)
                                                })}
                                                className="md3-icon-btn text-error"
                                            >
                                                <X style={{ fontSize: 18 }} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-outlineVariant">
                                <button type="submit" className="md3-btn-filled flex-1">
                                    {selectedTask ? 'Speichern' : 'Erstellen'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { setShowModal(false); resetForm(); }}
                                    className="md3-btn-text flex-1"
                                >
                                    Abbrechen
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tasks;
